import React, { useState, useEffect } from 'react';
import { Loader2, TrendingUp, Mail, AlertTriangle, Zap, BarChart3, ArrowRight } from 'lucide-react';
import { API_URL, fetchWithAuth } from './constants';

const RESPONSE_STYLES = {
  positive: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Replied' },
  pending: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Pending' },
  no_response: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'No Response' },
  unlinked: { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Unlinked' },
};

export const PitchAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const d = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pitch-email/analytics`);
        setData(d);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading pitch analytics...
      </div>
    );
  }

  if (!data || data.total_sent === 0) {
    return (
      <div className="p-6 bg-white border border-gray-200 rounded-xl text-center" data-testid="pitch-analytics-empty">
        <Mail className="h-8 w-8 text-gray-200 mx-auto mb-2" />
        <p className="text-sm text-gray-500 mb-1">No pitch emails sent yet</p>
        <p className="text-xs text-gray-400">Send your first pitch email to start tracking performance</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pitch-analytics">
      {/* Overview Metrics */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
          <p className="text-xl font-bold text-[#0A1F44]">{data.total_sent}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Pitches Sent</p>
        </div>
        <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
          <p className="text-xl font-bold text-emerald-600">{data.replied}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Replied</p>
        </div>
        <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
          <p className={`text-xl font-bold ${data.reply_rate >= 20 ? 'text-emerald-600' : data.reply_rate >= 10 ? 'text-amber-600' : 'text-red-500'}`}>
            {data.reply_rate}%
          </p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Reply Rate</p>
        </div>
        <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
          <p className="text-xl font-bold text-gray-400">{data.no_response}</p>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">No Response</p>
        </div>
      </div>

      {/* Insights */}
      {data.insights.length > 0 && (
        <div className="space-y-2" data-testid="pitch-insights">
          {data.insights.map((ins, i) => (
            <div key={i} className={`flex items-start gap-2.5 px-4 py-2.5 rounded-lg ${
              ins.type === 'positive' ? 'bg-emerald-50 border border-emerald-100' : 'bg-amber-50 border border-amber-100'
            }`}>
              {ins.type === 'positive'
                ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />}
              <p className={`text-xs font-medium leading-relaxed ${ins.type === 'positive' ? 'text-emerald-700' : 'text-amber-700'}`}>{ins.text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Performance Breakdowns — Side by Side */}
      <div className="grid grid-cols-2 gap-4">
        {/* By Pitch Type */}
        <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="pitch-type-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-3.5 w-3.5 text-[#D4AF37]" />
            <h4 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider">By Pitch Style</h4>
          </div>
          {['short', 'detailed'].map(type => {
            const d = data.by_pitch_type[type];
            return (
              <div key={type} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <span className="text-xs font-medium text-[#0A1F44] capitalize">{type}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-500">{d.sent} sent</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${d.reply_rate >= 20 ? 'bg-emerald-50 text-emerald-700' : d.reply_rate >= 10 ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                    {d.reply_rate}% reply
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* By Candidate Score */}
        <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="score-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-3.5 w-3.5 text-[#D4AF37]" />
            <h4 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider">By Candidate Score</h4>
          </div>
          {Object.entries(data.by_candidate_score).map(([range, d]) => (
            <div key={range} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <span className="text-xs font-medium text-[#0A1F44]">Score {range}</span>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-gray-500">{d.sent} sent</span>
                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${d.reply_rate >= 20 ? 'bg-emerald-50 text-emerald-700' : d.reply_rate >= 10 ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                  {d.reply_rate}% reply
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Roles */}
      {data.by_role.length > 0 && (
        <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="role-performance">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-3.5 w-3.5 text-[#D4AF37]" />
            <h4 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider">Performance by Role</h4>
          </div>
          <div className="space-y-1">
            {data.by_role.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-medium text-[#0A1F44] truncate">{r.title}</span>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{r.company}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-[10px] text-gray-500">{r.sent} sent</span>
                  <span className={`text-[10px] font-bold ${r.replied > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {r.replied} {r.replied > 0 ? 'replies' : 'replies'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Sends */}
      {data.recent_sends.length > 0 && (
        <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="recent-sends">
          <h4 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider mb-3">Recent Pitch Emails</h4>
          <div className="space-y-1.5">
            {data.recent_sends.map((s, i) => {
              const resp = RESPONSE_STYLES[s.response] || RESPONSE_STYLES.pending;
              return (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="h-3 w-3 text-gray-300 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-xs text-[#0A1F44] font-medium truncate block">{s.to_name || s.company}</span>
                      <span className="text-[10px] text-gray-400 truncate block">{s.subject}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[9px] text-gray-400 capitalize">{s.pitch_type}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${resp.bg} ${resp.text}`}>{resp.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
